import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  useColorModeValue,
  Image,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/tablet-login-concept-illustration_114360-7963.avif';
import baseUrl from 'Base_Url/baseUrl';
import { PinInput, PinInputField } from '@chakra-ui/react';

function SignIn() {
  const [step, setStep] = useState(1);
  const [phoneNo, setphoneNo] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingTime, setRemainingTime] = useState(120);

  const navigate = useNavigate();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const textPrimary = useColorModeValue('gray.600', 'gray.300');
  const textSecondary = useColorModeValue('gray.500', 'gray.300');
  const borderClr = useColorModeValue('gray.300', 'gray.600');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const placeholderColor = useColorModeValue('gray.400', 'gray.500');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phoneNo || phoneNo.length !== 10) {
      toast({
        title: 'Enter a valid 10-digit mobile number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${baseUrl}/auth/otp`, {
        phoneNo: phoneNo,
      });

      if (response.data.status) {
        toast({
          title: `OTP sent to ${phoneNo}`,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        setStep(2);
        setRemainingTime(120);
      } else {
        toast({
          title: 'Failed to send OTP',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Server error while sending OTP',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        phoneNo: phoneNo,
        otp: otp,
      };

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const response = await axios.post(
        `${baseUrl}/auth/login`,
        dataToSend,
        config,
      );

      if (
        response.data.code === 200 &&
        response.data?.data?.user.role === 'talati'
      ) {
        toast({
          title: 'Talati user is not permitted!',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      } else if (response.data.code === 200) {
        const token = response.data?.data?.tokens?.access?.token;
        const user = response.data?.data?.user;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        toast({
          title: 'Login Successful',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setTimeout(() => {
          navigate('/admin/dashboard');
          window.location.reload();
        }, 500);
      } else {
        toast({
          title: 'Invalid OTP',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error verifying OTP',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      console.error('Verify OTP Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer;
    if (step === 2 && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
    } else if (remainingTime === 0) {
      toast({
        title: 'OTP expired',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    return () => clearInterval(timer);
  }, [step, remainingTime, toast]);

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        align="center"
        justify="flex-start"
        h="100vh"
        w="100%"
        px={{ base: '16', md: '15' }}
      >
        <Box
          bg={cardBg}
          p={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="lg"
          w={{ base: '100%', md: '400px' }}
        >
          <Image
            src="/logo.png"
            alt="Zudpi Land Survey Logo"
            mx="auto"
            maxH="120px"
            padding="10px"
          />
          <Text fontSize="sm" color={textSecondary} mb={6} textAlign="center">
            {step === 1 ? 'Enter your mobile number to receive OTP' : ''}
          </Text>

          <form onSubmit={step === 1 ? handleSendOtp : handleVerifyOtp}>
            {step === 1 ? (
              <FormControl mb={4}>
                <FormLabel fontSize="sm" color={textPrimary}>
                  Mobile Number
                </FormLabel>
                <Input
                  type="tel"
                  placeholder="Enter mobile number"
                  value={phoneNo}
                  onChange={(e) => setphoneNo(e.target.value)}
                  maxLength={10}
                  color={inputTextColor}
                  _placeholder={{ color: placeholderColor }}
                />

              </FormControl>
            ) : (
              <FormControl mb={4}>
                <FormLabel fontSize="sm" color={textPrimary} textAlign="center">
                  <Text fontSize="sm" color={textSecondary} mb={6}>
                    Please enter the 4-digit OTP
                    <br />
                    sent to ****{phoneNo.slice(-4)} mobile number
                  </Text>
                </FormLabel>

                <Flex justify="center" gap={3} mb={2}>
                  <PinInput
                    otp
                    value={otp}
                    onChange={setOtp}
                    type="number"
                  >
                    {Array(4)
                      .fill(0)
                      .map((_, index) => (
                        <PinInputField
                          key={index}
                          border="2px solid"
                          borderColor={borderClr}
                          borderRadius="md"
                          _focus={{ borderColor: 'blue.400' }}
                          _hover={{ borderColor: 'blue.300' }}
                          fontSize="2xl"
                          w="60px"
                          h="60px"
                          textAlign="center"
                          lineHeight="60px"
                          fontWeight="bold"
                          padding="0"

                          color={inputTextColor}
                          _placeholder={{ color: placeholderColor }}
                        />
                      ))}
                  </PinInput>
                </Flex>

                <Flex justify="center" mb={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    onClick={() => {
                      setStep(1);
                      setOtp('');
                    }}
                  >
                    Change Mobile Number
                  </Button>
                </Flex>

                {remainingTime > 0 ? (
                  <Text fontSize="sm" color="red.400" textAlign="center">
                    OTP will expire in: {Math.floor(remainingTime / 60)}:
                    {String(remainingTime % 60).padStart(2, '0')}
                  </Text>
                ) : (
                  <Flex justify="center">
                    <Button
                      size="sm"
                      mt={1}
                      variant="link"
                      colorScheme="blue"
                      onClick={handleSendOtp}
                    >
                      Resend OTP
                    </Button>
                  </Flex>
                )}
              </FormControl>
            )}

            <Button
              type="submit"
              colorScheme="blue"
              width="100%"
              isLoading={loading}
              mb={3}
            >
              {step === 1 ? 'Send OTP' : 'Verify OTP'}
            </Button>
          </form>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
